import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : `${window.location.origin}/api`);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ────────────────────────────────────────────────────────────────────────

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// ── Super Admin ─────────────────────────────────────────────────────────────────

export const superadminAPI = {
  listTenants: async () => {
    const response = await api.get('/superadmin/tenants');
    return response.data;
  },
  createTenant: async (name) => {
    const response = await api.post('/superadmin/tenants', { name });
    return response.data;
  },
  toggleTenant: async (tenantId) => {
    const response = await api.patch(`/superadmin/tenants/${tenantId}/toggle`);
    return response.data;
  },
  listTenantUsers: async (tenantId) => {
    const response = await api.get(`/superadmin/tenants/${tenantId}/users`);
    return response.data;
  },
  createTenantUser: async (tenantId, userData) => {
    const response = await api.post(`/superadmin/tenants/${tenantId}/users`, userData);
    return response.data;
  },
  deleteTenantUser: async (tenantId, userId) => {
    const response = await api.delete(`/superadmin/tenants/${tenantId}/users/${userId}`);
    return response.data;
  },
};

// ── Admin ───────────────────────────────────────────────────────────────────────

export const adminAPI = {
  listUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },
  updateUser: async (userId, updates) => {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  downloadBackup: async (password) => {
    const data = new FormData();
    data.append('password', password);
    const response = await api.post('/admin/backup', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    return response.data;
  },
  restoreBackup: async (file, password) => {
    const data = new FormData();
    data.append('backup_file', file);
    data.append('password', password);
    const response = await api.post('/admin/backup/restore', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ── Samples ─────────────────────────────────────────────────────────────────────

export const sampleAPI = {
  getSamples: async (params = {}) => {
    const response = await api.get('/samples/', { params });
    return response.data;
  },

  getSample: async (id) => {
    const response = await api.get(`/samples/${id}`);
    return response.data;
  },

  createSample: async (formData, onUploadProgress) => {
    const response = await api.post('/samples/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  },

  updateSample: async (id, formData, onUploadProgress) => {
    const response = await api.put(`/samples/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  },

  duplicateSample: async (id) => {
    const response = await api.post(`/samples/${id}/duplicate`);
    return response.data;
  },

  deleteSample: async (id) => {
    const response = await api.delete(`/samples/${id}`);
    return response.data;
  },

  generatePDF: async (id) => {
    const response = await api.get(`/samples/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  generateQRLabel: async (id) => {
    const response = await api.get(`/samples/${id}/label`, { responseType: 'blob' });
    return response.data;
  },

  getImageUrl: (filename) => {
    if (!filename) return null;
    if (filename.toLowerCase().endsWith('.tiff') || filename.toLowerCase().endsWith('.tif')) {
      return `${API_BASE_URL}/image-preview/${filename}`;
    }
    return `${API_BASE_URL}/uploads/${filename}`;
  },

  getOriginalFileUrl: (filename) => {
    if (!filename) return null;
    return `${API_BASE_URL}/download-original/${filename}`;
  },

  createTiffPreviews: async () => {
    const response = await api.post('/create-tiff-previews');
    return response.data;
  },
};

export default api;
